import { useState } from 'react';
import { AlertTriangle, AlertCircle, X } from 'lucide-react';
import type { LotFlagType } from '../../types/agedStock';

interface LotFlagModalProps {
    productName: string;
    lotCode: string;
    flagType: LotFlagType;
    action: 'set' | 'remove';
    onConfirm: (reason: string) => void;
    onCancel: () => void;
}

export function LotFlagModal({ productName, lotCode, flagType, action, onConfirm, onCancel }: LotFlagModalProps) {
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const isExpired = flagType === 'Expired';
    const Icon = isExpired ? AlertTriangle : AlertCircle;
    const title = action === 'set'
        ? (isExpired ? 'Mark Lot as Expired' : 'Flag Quality Issue')
        : (isExpired ? 'Remove Expired Flag' : 'Remove Quality Issue Flag');

    const reasonRequired = action === 'set';

    function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (reasonRequired && !reason.trim()) return;
        setSubmitting(true);
        onConfirm(reason.trim());
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-md rounded-lg bg-white shadow-lg">
                <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                    <h2 className={`flex items-center gap-2 text-base font-semibold ${isExpired ? 'text-red-700' : 'text-purple-700'}`}>
                        <Icon className="h-5 w-5" />
                        {title}
                    </h2>
                    <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="px-4 py-3 space-y-3">
                    <p className="text-sm text-gray-700">
                        <span className="font-medium">Product:</span> {productName}<br />
                        <span className="font-medium">Lot:</span> <code className="rounded bg-gray-100 px-1 py-0.5">{lotCode}</code>
                    </p>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">
                            Reason {reasonRequired && <span className="text-red-500">*</span>}
                        </label>
                        <textarea
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            rows={3}
                            maxLength={500}
                            required={reasonRequired}
                            placeholder={isExpired ? 'Why is this lot considered expired?' : 'Describe the quality issue'}
                            className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none"
                        />
                        <p className="mt-1 text-xs text-gray-500">{reason.length}/500</p>
                    </div>

                    <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting || (reasonRequired && !reason.trim())}
                            className={`rounded-md px-3 py-1.5 text-sm text-white disabled:opacity-50 ${
                                isExpired ? 'bg-red-600 hover:bg-red-700' : 'bg-purple-600 hover:bg-purple-700'
                            }`}
                        >
                            {action === 'set' ? 'Set Flag' : 'Remove Flag'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
